import * as React from 'react';

import styled from 'styled-components';
import { isJsmCustomerSsoEapEnabled } from 'feature-flags';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { NoteContent } from 'view/layout/note-content';
import { h700, h600 } from 'view/styles/fonts';
import type { ButtonAppearances } from '@atlaskit/button';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { DocumentTitle } from '@atlassian/help-center-common-component/document-title';
import { TextField } from '@atlassian/help-center-common-component/fields/text-field';
import ThemedBackButton from '@atlassian/help-center-common-component/themed-back-button';
import { ThemedButton } from '@atlassian/help-center-common-component/themed-button';

import { ThemedLink } from '@atlassian/help-center-common-component/themed-link';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import type { Error } from '@atlassian/help-center-common-util/errors';
import messages from './messages';

interface SignUpFormProps {
    emailAddress: string;
    title: string;
    requestSummary: string | null;
    portalId: string | undefined;
    baseUrl: string;
    isSubmitting: boolean;
    error: Error | undefined;
    hasSingleSignOnAvailable: boolean;
    hasPasswordAvailable: boolean;
    ssoRedirectUrl?: string;
    onSubmitButtonClick: () => void;
    onBackButtonClick: () => void;
}

const isRequestInviteSignUp = (props: SignUpFormProps): boolean => {
    const { emailAddress, requestSummary } = props;
    return requestSummary !== null && requestSummary.length > 0 && emailAddress !== null && emailAddress.length > 0;
};

const ErrorMessage = (props: { error: Error | undefined }) => {
    const { error } = props;
    const { formatMessage } = useIntl();

    if (error) {
        let errorMessage;
        if ('errorMessage' in error) {
            errorMessage = error.errorMessage;
        } else {
            errorMessage = formatMessage(error.description);
        }
        return <SectionMessage appearance="error">{errorMessage}</SectionMessage>;
    }
    return null;
};

const SingleSignOnCallToAction = (props: { ssoRedirectUrl?: string }) => {
    const { ssoRedirectUrl } = props;
    const { formatMessage } = useIntl();

    const privacyPolicyLink = (
        <ThemedLink
            actionSubjectId="privacyPolicyLinkSSO"
            href="https://www.atlassian.com/legal/privacy-policy"
            target="_blank"
        >
            {formatMessage(messages.privacyPolicyLinkText)}
        </ThemedLink>
    );
    const noticeAndDisclaimerLink = (
        <ThemedLink
            actionSubjectId="noticeAndDisclaimerLinkSSO"
            href="https://www.atlassian.com/legal/user-notice#atlassian-user-notice"
            target="_blank"
        >
            {formatMessage(messages.noticeAndDisclaimerLinkText)}
        </ThemedLink>
    );

    return (
        <>
            <Disclaimer>
                <FormattedMessage
                    {...messages.continueWithSingleSignOnDisclaimer}
                    values={{ privacyPolicyLink, noticeAndDisclaimerLink }}
                />
            </Disclaimer>
            <ThemedButton
                id="signup-with-sso-link-button"
                type="submit"
                appearance="primary"
                shouldFitContainer
                actionSubjectId="signupWithSingleSignOn"
                isDisabled={ssoRedirectUrl === undefined}
                href={ssoRedirectUrl}
            >
                {formatMessage(messages.continueWithSingleSignOnButton)}
            </ThemedButton>
        </>
    );
};

const PasswordCallToAction = (props: {
    isSubmitting: boolean;
    onSubmitButtonClick: () => void;
    appearance: ButtonAppearances;
}) => {
    const { formatMessage } = useIntl();

    const { isSubmitting, onSubmitButtonClick, appearance } = props;

    return (
        <ThemedButton
            id="send-link-button"
            type="submit"
            appearance={appearance}
            shouldFitContainer
            isLoading={isSubmitting}
            actionSubjectId="sendSignUpLink"
            onClick={onSubmitButtonClick}
        >
            {formatMessage(messages.signupButtonLabel)}
        </ThemedButton>
    );
};

export const SignUpStateless = (props: SignUpFormProps) => {
    const {
        title,
        emailAddress,
        onBackButtonClick,
        onSubmitButtonClick,
        isSubmitting,
        error,
        ssoRedirectUrl,
        hasSingleSignOnAvailable,
        hasPasswordAvailable,
    } = props;
    const { formatMessage } = useIntl();
    const emailLabelAndPlaceholder = formatMessage(messages.emailLabelAndPlaceholder);
    const shouldRenderBackButton = !isRequestInviteSignUp(props);

    return (
        <DocumentTitle title={formatMessage(messages.documentTitle)}>
            <NoteContent isLoading={false} isCentered={false}>
                <FireScreenEventOnMount />
                {shouldRenderBackButton && (
                    <ThemedBackButton
                        appearance="link"
                        actionSubjectId={'backToLoginButton'}
                        onClick={onBackButtonClick}
                    >
                        {formatMessage(messages.backButtonLabel)}
                    </ThemedBackButton>
                )}
                <SignupTitle>{title}</SignupTitle>
                <ErrorMessage error={error} />
                <SignupSubtitle>{formatMessage(messages.signUpSubTitle)}</SignupSubtitle>
                <SignupContainer id="sign-up-form">
                    <TextField
                        id="user-email"
                        shouldFitContainer
                        placeholder={emailLabelAndPlaceholder}
                        label={emailLabelAndPlaceholder}
                        isLabelHidden={false}
                        disabled
                        defaultValue={emailAddress}
                    />
                    {!isJsmCustomerSsoEapEnabled() && (
                        <ThemedButton
                            id="send-link-button"
                            type="submit"
                            appearance="primary"
                            shouldFitContainer
                            isLoading={isSubmitting}
                            actionSubjectId="sendSignUpLink"
                            // Disabling existing violations, should be fixed when revisited.
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            onClick={onSubmitButtonClick}
                        >
                            {formatMessage(messages.signupButtonLabel)}
                        </ThemedButton>
                    )}
                    {isJsmCustomerSsoEapEnabled() && hasSingleSignOnAvailable && (
                        <SingleSignOnCallToAction ssoRedirectUrl={ssoRedirectUrl} />
                    )}
                    {isJsmCustomerSsoEapEnabled() && hasPasswordAvailable && (
                        <SignupContainerFooter>
                            <PasswordCallToAction
                                isSubmitting={isSubmitting}
                                onSubmitButtonClick={onSubmitButtonClick}
                                appearance={hasSingleSignOnAvailable ? 'link' : 'primary'}
                            />
                        </SignupContainerFooter>
                    )}
                </SignupContainer>
            </NoteContent>
        </DocumentTitle>
    );
};

export default SignUpStateless;

const SignupTitle = styled.div`
    ${h700};
    margin-bottom: ${token('space.300', '24px')};
`;

const SignupSubtitle = styled.div`
    ${h600};
`;

const SignupContainer = styled.div`
    text-align: left;

    > :not(:last-child) {
        margin-bottom: ${token('space.300', '24px')};
    }
`;

const SignupContainerFooter = styled.div`
    text-align: center;
`;

const Disclaimer = styled.div`
    margin-bottom: ${token('space.300', '24px')};
`;
